import React, { useState } from 'react';

import {
  Alert,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Chip,
  Stack,
} from '@mui/material';
import { AccessTime, Add, Delete, Extension, RemoveCircleOutline } from '@mui/icons-material';
import { useMutation, useQuery } from '@apollo/client';
import { ADD_APP_LINK, REMOVE_APP_LINK, GET_STANDALONE_APPS } from './guide.graphql';
import { ColWidths, TableCellHead } from '../components/TableComponents';
import { StyledPaper } from '../theme';
import LoadingSpinner from '../LoadingSpinner';
import { StyledLink } from '../Components';
import { Guide } from './Details';

/**
 * Applications related to the guide, laid out like the Bøker panel it sits next to. Two separate
 * lists on purpose: the ones an editor picked by hand, and the ones read out of the CMS content.
 * Only the first is editable. The CMS list shows the saved content and the published version
 * together, as the guide links do: an app only in the content is marked as not published yet, and
 * one only in the published version as going at the next publish.
 */
export function AppLinks({ guide }: { guide: Guide }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [remove, { error: removeError }] = useMutation(REMOVE_APP_LINK);
  const appLinks = [...(guide.appLinks ?? [])].sort((a, b) => a?.name?.localeCompare(b?.name ?? '') ?? 0);
  const cmsApps = [...(guide.cmsApps ?? [])].sort((a, b) => a?.name?.localeCompare(b?.name ?? '') ?? 0);

  const handleRemove = async (appId: string | undefined) => {
    try {
      await remove({ variables: { input: { id: guide.id, appId } } });
    } catch {
      // The error is shown in the panel from the mutation's state.
    }
  };

  return (
    <StyledPaper>
      <Typography variant="h5" component="h2">
        Applikasjoner&nbsp;
        <IconButton onClick={() => setPickerOpen(true)} size="small" aria-label="Legg til applikasjon">
          <Add />
        </IconButton>
      </Typography>
      {removeError && <Alert severity="error">Kunne ikke fjerne applikasjonen. Prøv igjen.</Alert>}
      <List dense={true}>
        {appLinks.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            Ingen applikasjoner valgt
          </Typography>
        )}
        {appLinks.map((link) => (
          <ListItem
            key={link?.appId}
            dense
            secondaryAction={
              <IconButton edge="end" size="small" aria-label={`Fjern ${link?.name}`} onClick={() => handleRemove(link?.appId)}>
                <Delete fontSize="small" />
              </IconButton>
            }>
            <ListItemIcon>
              <Extension />
            </ListItemIcon>
            <ListItemText primary={<StyledLink to={`/app/${link?.appId}`}>{link?.name}</StyledLink>} secondary={link?.category} />
          </ListItem>
        ))}
      </List>

      {guide.useCmsContent && (
        <>
          <Typography variant="subtitle2" component="h3" color="textSecondary" sx={{ mt: 1 }}>
            Inkludert i anvisningen
          </Typography>
          <List dense={true}>
            {guide.cmsApps === null && (
              <Typography variant="body2" color="textSecondary">
                Kunne ikke hente applikasjonene. Applikasjonstjenesten svarer ikke.
              </Typography>
            )}
            {guide.cmsApps !== null && cmsApps.length === 0 && (
              <Typography variant="body2" color="textSecondary">
                Ingen applikasjoner inkludert
              </Typography>
            )}
            {cmsApps.map((app) => (
              <ListItem key={app?.appId} dense>
                <ListItemIcon>
                  <Extension />
                </ListItemIcon>
                {/* The chip sits in the text flow under the name rather than as a secondaryAction, which is
                    positioned over the text and only leaves room for an icon button. */}
                <ListItemText
                  primary={<StyledLink to={`/app/${app?.appId}`}>{app?.name}</StyledLink>}
                  secondaryTypographyProps={{ component: 'div' }}
                  secondary={
                    <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                      {app?.category}
                      {!app?.isPublished && (
                        <Chip label="Upublisert" size="small" variant="outlined" icon={<AccessTime />} title="Lagt inn i innholdet, men ikke publisert ennå" />
                      )}
                      {app?.isPublished && !app?.isInContent && (
                        <Chip
                          label="Fjernes ved publisering"
                          size="small"
                          variant="outlined"
                          icon={<RemoveCircleOutline />}
                          title="Tatt ut av innholdet, men leserne har den til anvisningen publiseres igjen"
                        />
                      )}
                    </Stack>
                  }
                />
              </ListItem>
            ))}
          </List>
        </>
      )}

      {pickerOpen && <AppPicker guide={guide} onClose={() => setPickerOpen(false)} />}
    </StyledPaper>
  );
}

function AppPicker({ guide, onClose }: { guide: Guide; onClose(): void }) {
  const { data, loading } = useQuery(GET_STANDALONE_APPS);
  const [add, { error: addError }] = useMutation(ADD_APP_LINK);
  const [remove, { error: removeError }] = useMutation(REMOVE_APP_LINK);

  // The link points at the app family, so every version of an app is one row here.
  const families = Object.values(
    (data?.standaloneApps ?? []).reduce<Record<string, { appId: string; name: string; category: string; version: number }>>((acc, app) => {
      if (!app?.appId) return acc;
      const seen = acc[app.appId];
      if (!seen || (app.version ?? 0) > seen.version) {
        acc[app.appId] = { appId: app.appId, name: app.name ?? '', category: app.category ?? '', version: app.version ?? 0 };
      }
      return acc;
    }, {}),
  ).sort((a, b) => a.name.localeCompare(b.name));

  const isSelected = (appId: string) => guide.appLinks?.some((x) => x?.appId === appId) ?? false;
  const handleToggle = async (app: { appId: string; name: string }) => {
    try {
      if (isSelected(app.appId)) {
        await remove({ variables: { input: { id: guide.id, appId: app.appId } } });
      } else {
        await add({ variables: { input: { id: guide.id, appId: app.appId, name: app.name } } });
      }
    } catch {
      // The error is shown in the dialog from the mutation's state.
    }
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth={true}>
      <DialogTitle>Relevante applikasjoner</DialogTitle>
      <DialogContent>
        {(addError || removeError) && <Alert severity="error">Kunne ikke endre koblingen. Prøv igjen.</Alert>}
        {loading && <LoadingSpinner />}
        {!loading && families.length === 0 && <Typography>Ingen selvstendige applikasjoner tilgjengelig</Typography>}
        {families.length > 0 && (
          <Table size="small">
            <ColWidths widths={[60, null, 160, 80]} />
            <TableHead>
              <TableRow>
                <TableCellHead>Valgt</TableCellHead>
                <TableCellHead>Navn</TableCellHead>
                <TableCellHead>Kategori</TableCellHead>
                <TableCellHead>Versjon</TableCellHead>
              </TableRow>
            </TableHead>
            <TableBody>
              {families.map((app) => {
                const selected = isSelected(app.appId);
                return (
                  <TableRow hover key={app.appId} onClick={() => handleToggle(app)} role="checkbox" aria-checked={selected} selected={selected}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selected} color="primary" />
                    </TableCell>
                    <TableCell>{app.name}</TableCell>
                    <TableCell>{app.category}</TableCell>
                    <TableCell>{app.version}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Lukk</Button>
      </DialogActions>
    </Dialog>
  );
}
